// ui/useSettings.js: Preact hook over the settings storage layer.
//
// loadSettings on mount, applyFontSetting on settings.system_font
// changes, and a stable `update` callback that merges + persists. The
// returned settings reference changes on every update so consumers
// re-render naturally.

import { useState, useEffect, useCallback } from 'preact/hooks';
import {
  loadSettings, saveSettings, applyFontSetting, applyReducedMotionSetting, reducedMotionActive, watchReducedMotion,
} from '../settings.js';
import { setCanvasSystemFont } from '../canvas/text.js';
import { setAudioTier } from '../audio/index.js';
import { applyCrtTier, effectiveTier, prefersReducedMotion } from './crt-shader.js';

// F7: every useSettings instance kept its own useState copy, so a change
// made in the in-game panel left the lobby panel showing stale toggles
// until reload (RoomView and NetworkedGameView each hold an instance).
// A module-level subscriber set fans every update out to all live
// instances in this tab, so they stay in lock-step.
/** @type {Set<(s: import('../settings.js').Settings) => void>} */
const settingsListeners = new Set();

/**
 * @returns {[import('../settings.js').Settings, (patch: Partial<import('../settings.js').Settings>) => void]}
 */
export function useSettings() {
  const [settings, setSettings] = useState(() => loadSettings());

  // Subscribe to cross-instance updates for this tab.
  useEffect(() => {
    settingsListeners.add(setSettings);
    // Re-sync on mount in case another instance changed settings while
    // this one was unmounted (e.g. the lobby panel after an in-game edit).
    setSettings(loadSettings());
    return () => { settingsListeners.delete(setSettings); };
  }, []);

  // Apply font setting whenever it changes (covers initial mount too).
  // M5 #1: also mirrors into canvas/text.js so canvas text follows the
  // same toggle as the DOM body class.
  useEffect(() => {
    applyFontSetting(settings.system_font);
    setCanvasSystemFont(settings.system_font);
  }, [settings.system_font]);

  // Restyle 3A: body.reduce-motion is on while either the OS setting or
  // the in-app switch asks for reduced motion, so the cream button press
  // and the hero float stand down. Both sources are watched for changes.
  const [osReduced, setOsReduced] = useState(() => prefersReducedMotion());
  useEffect(() => watchReducedMotion(setOsReduced), []);
  useEffect(() => {
    applyReducedMotionSetting(reducedMotionActive(settings.reduced_motion_override, osReduced));
  }, [settings.reduced_motion_override, osReduced]);

  // M5 #3: keep the audio engine's tier in sync with settings. The engine
  // is constructed with the initial tier in audio/index.js; this effect
  // updates it on subsequent changes.
  useEffect(() => {
    setAudioTier(settings.sound_tier);
  }, [settings.sound_tier]);

  // M5 #8: apply CRT tier to <html>, gated by prefers-reduced-motion.
  // App.jsx applies the initial tier from settings; this effect picks
  // up subsequent user toggles in the settings panel.
  useEffect(() => {
    applyCrtTier(effectiveTier(settings.crt, prefersReducedMotion()));
  }, [settings.crt]);

  const update = useCallback(
    /** @param {Partial<import('../settings.js').Settings>} patch */
    (patch) => {
      const next = saveSettings(patch);
      // Notify every live instance (including this one) so all panels
      // reflect the change immediately, no reload to un-stale the lobby.
      for (const listener of settingsListeners) listener(next);
    },
    []
  );

  return [settings, update];
}
